import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Detects when a newer frontend build has been deployed while this tab is still
 * running the old one. A single-page app never re-downloads itself during
 * client-side navigation, so after a deploy users keep running the stale
 * bundle — pages render old data/markup or lazy chunks 404 (ChunkLoadError).
 *
 * SCRUM-1961 — this used to `location.reload()` automatically the moment a new
 * build was detected, which yanked the page out from under a reader mid-view.
 * It now NEVER auto-reloads: on detecting a newer build it raises
 * `updateAvailable$`, and the app-shell shows a dismissible "refresh available"
 * banner. The reload only happens when the user opts in via `applyUpdate()`
 * (or the app can apply it at a safe moment such as a fresh navigation). This
 * keeps the stale-bundle safety net without the disruptive surprise reload.
 *
 * Mechanism: fingerprint the hashed .js filenames in index.html at startup,
 * then re-fetch index.html on navigation; if the fingerprint changed a new
 * build is live.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private baseline: string | null = null;
  private inFlight = false;
  private readonly _updateAvailable = new BehaviorSubject<boolean>(false);

  /** Emits `true` once a newer build has been detected. Never auto-reloads. */
  get updateAvailable$(): Observable<boolean> {
    return this._updateAvailable.asObservable();
  }
  get isUpdateAvailable(): boolean {
    return this._updateAvailable.value;
  }

  /** Capture the build fingerprint this tab is running. Call once on startup. */
  async init(): Promise<void> {
    this.baseline = await this.fetchFingerprint();
  }

  /**
   * If a newer build is live, flag it (does NOT reload). The app-shell listens
   * to `updateAvailable$` and prompts the user; the actual reload is deferred
   * to `applyUpdate()` so it can never interrupt reading. Safe to call on every
   * navigation — once flagged it stays flagged and re-checks are cheap no-ops.
   */
  async reloadIfStale(): Promise<void> {
    if (this.inFlight || this._updateAvailable.value) return;
    this.inFlight = true;
    try {
      if (!this.baseline) { this.baseline = await this.fetchFingerprint(); return; }
      const current = await this.fetchFingerprint();
      if (!current || current === this.baseline) return;
      // A newer build is live — surface it, but leave the reader in place.
      this._updateAvailable.next(true);
    } catch { /* network/parse issue — leave the tab as-is */ }
    finally { this.inFlight = false; }
  }

  /** User-consented reload to pick up the new build. The only reload path. */
  applyUpdate(): void {
    location.reload();
  }

  private async fetchFingerprint(): Promise<string | null> {
    try {
      const html = await (await fetch('/', { cache: 'no-store' })).text();
      // Any change in the set of hashed chunk filenames ⇒ a new build.
      const names = html.match(/[a-zA-Z0-9._-]+\.js/g) ?? [];
      return names.length ? names.sort().join(',') : null;
    } catch {
      return null;
    }
  }
}
